export interface IndividualInfo {
  indvId: string;
  firstName: string;
  midInitial: string | null;
  lastName: string;
  gender: string;
  dob: string;
  eligForMatsAppealSw?: string;
}

export interface MedAplCaseIndvData {
  caseNum: string;
  indvInfo: IndividualInfo[];
}

export interface MedAppealAddress {
  addressFormat: any;
  address: any;
  addressType: any;
}

export interface TccMedAplRqstInfoData {
  appNum: number | null;
  caseNum: number | null;
  indvId: number | null;
  filingMaForCd: any;
  callerRelationshipCd: any;
  callerFstName: string | null;
  callerLastName: string | null;
  callerPhnNum: number | null;
  callerPhnNumExt: number | null;
  callerPrimLangCd: any;
  isMemberPresentSw: any;
  medAppealAddress: MedAppealAddress | null;
  resSameAsMailAddrSw: any;
}

// UI-specific Interfaces
export interface MemberRow {
  indvid: string;
  memberName: string;
  personId: string;
  selected: boolean;
  firstName?: string;
  lastName?: string;
  gender?: string;
  dob?: string;
  eligForMatsAppealSw?: string;
}

export interface CaseSummary {
  caseNum: string;
  loginIndvId: string;
  indvSummaryResponse?: Array<{ indvId: string; memberName: string }>;
}

export interface FilerContactData {
  relationship: string | null;
  name: { firstName: string | null; lastName: string | null };
  phoneNumber: string | null;
  extension: string | null;
  primaryLanguage: string | null;
  memberPresent: string | null;
  addressFormat: string | null;
  filerAddressSameAsMember: string | null;
}

// Enums
export enum FilingCode {
  Self = 'SLF',
  OtherHouseholdMemberMedical = 'OTH'
}

export enum ControlPath {
  FilingCaseFor = 'data.tccMedAplRqstInfoData.filingMaForCd',
  AppealCaseNumber = 'data.appealCaseNumber',
  CaseNum = 'data.tccMedAplRqstInfoData.caseNum',
  IndvId = 'data.tccMedAplRqstInfoData.indvId',
  SelectedMember = 'extras.helpers.selectedMemberValidation',
  CallerRelationship = 'data.tccMedAplRqstInfoData.callerRelationshipCd',
  CallerFirstName = 'data.tccMedAplRqstInfoData.callerFstName',
  CallerLastName = 'data.tccMedAplRqstInfoData.callerLastName',
  CallerPhone = 'data.tccMedAplRqstInfoData.callerPhnNum',
  CallerPhoneExt = 'data.tccMedAplRqstInfoData.callerPhnNumExt',
  CallerLanguage = 'data.tccMedAplRqstInfoData.callerPrimLangCd',
  MemberPresent = 'data.tccMedAplRqstInfoData.isMemberPresentSw',
  MedAppealAddress = 'data.tccMedAplRqstInfoData.medAppealAddress',
  AddressFormat = 'data.tccMedAplRqstInfoData.medAppealAddress.addressFormat',
  Address = 'data.tccMedAplRqstInfoData.medAppealAddress.address',
  AddressSame = 'data.tccMedAplRqstInfoData.resSameAsMailAddrSw',
  MedAplCaseIndvData = 'data.medAplCaseIndvData',
  LoggedInIndv = 'data.loggedInIndv'
}

export const EMPTY_FILER_CONTACT: Readonly<FilerContactData> = {
  relationship: null,
  name: { firstName: null, lastName: null },
  phoneNumber: null,
  extension: null,
  primaryLanguage: null,
  memberPresent: null,
  addressFormat: null,
  filerAddressSameAsMember: null
} as const;

// Column Definitions
export const SELECT_APPELLANTS_COLUMNS = [
  { name: 'HOUSEHOLD_MEMBER_NAME', prop: 'memberName' },
  { name: 'PERSON_ID', prop: 'personId' }
] as const;

// Medical Appeal Details
export const APPEAL_DETAIL_FIELDS = {
  OUT_OF_POCKET: [
    'data.paidServiceTypeCd',
    'data.paidServiceDtlsTxt'
  ] as const,

  BILLING: [
    'data.billedServiceTypeCd',
    'data.billedServiceDtlsTxt'
  ] as const,

  DENIED_SERVICE: [
    'data.deniedServiceTypeCd',
    'data.aplDtlsMedDentalTxt'
  ] as const,

  PHARMACY_CHANGE: [
    'data.currentPharmacyChngRsnTxt',
    'data.rqstdPharmacyTxt',
    'data.pharmacyChngRsnTxt'
  ] as const,

  PHARMACY_REMOVE: [
    'data.currentPharmacyRmvLockinTxt',
    'data.rmvLockinRsnTxt'
  ] as const
} as const;

// Textarea fields that need character validation
export const TEXTAREA_FIELDS = [
  'data.paidServiceDtlsTxt',
  'data.billedServiceDtlsTxt',
  'data.aplDtlsMedDentalTxt',
  'data.aplDtlsNoLockinRsnTxt',
  'data.currentPharmacyChngRsnTxt',
  'data.rqstdPharmacyTxt',
  'data.pharmacyChngRsnTxt',
  'data.currentPharmacyRmvLockinTxt',
  'data.rmvLockinRsnTxt',
  'data.inHomeCareTypeOthTxt'
] as const;